import type {
  CrudServiceListResponse,
  CurationTool,
  CurationToolCrudService,
  Identifiable,
  Queryable,
} from '@empathyco/playboard-core-domain'
import type { ActionContext } from 'vuex'
import type { ActionClass, ToolingState } from '../../../models'
import type { CrudServiceProvider } from '../../../providers/interfaces'
import { CONTAINER } from '../../../config/inversify.config'
import { TYPES } from '../../../config/inversify.types'
import { FeatureIdEnum } from '../../../enums/feature-id.enum'

type FindConflictingItemsResponse = CrudServiceListResponse<Identifiable & { criteria: Queryable }>

interface FindConflictingItemsParams {
  tool: string
  params?: {
    queries: Array<string>
    filters?: Record<string, unknown>

    [key: string]: unknown
  }
}

const isBeaconTool = (tool: string): boolean => {
  return tool === FeatureIdEnum.RELATED_TAGS || tool === FeatureIdEnum.NEXT_QUERIES
}

export class FindConflictingItemsAction implements ActionClass {
  static execute(
    this: void,
    _: ActionContext<ToolingState, unknown>,
    { tool, params }: FindConflictingItemsParams,
  ): Promise<FindConflictingItemsResponse> {
    const service = CONTAINER.get<CrudServiceProvider>(TYPES.CrudServiceProvider)(tool)

    const { queries = [], ...restOfparams } = params ?? { filters: {} }

    return Promise.all(
      queries?.map(query => {
        if (isBeaconTool(tool)) {
          return (
            service as unknown as CurationToolCrudService<CurationTool>
          ).findItemsMatchingCriteria({
            ...restOfparams.filters,
            query,
          })
        }
        return service.list({ ...restOfparams, filters: { ...restOfparams.filters, query } })
      }) as Array<Promise<FindConflictingItemsResponse>>,
    ).then((responses: Array<FindConflictingItemsResponse>): FindConflictingItemsResponse => {
      return responses.reduce(
        (responses: FindConflictingItemsResponse, response: FindConflictingItemsResponse) => {
          if (response.numFound === 0) {
            return responses
          }

          const items = response.results.filter(result => {
            const existTheQuery = result?.criteria.queries?.some(query => queries.includes(query))
            const wasNotAdded = !responses.results.some(item => item.id === result.id)
            return existTheQuery && wasNotAdded
          })

          if (items.length > 0) {
            responses.results.push(...items)
            responses.numFound = responses.results.length
          }

          return responses
        },
        {
          numFound: 0,
          results: [],
        },
      )
    })
  }
}
